/**
 * List of available properties and events https://github.com/23/GlueFrame/issues/5
 */

const glueFrame = (iframe: any, appName: string) => {
  // Allow posting messages only to the domain of the app
  const _domain = `${iframe.src}`.split('/').slice(0, 3).join('/')
  let ready = false

  const glueFrameId = Math.floor(new Date().getTime() * Math.random())
  let _callbackCount = 0
  const _callbacks: any = {}
  let _queue: any[] = []

  // Store callback functions in the parent window
  const _registerCallback = (callback: ({ ...any }) => void = () => {}) => {
    const callbackIdentifier = `${glueFrameId}_${++_callbackCount}`

    if (typeof callback === 'function') {
      _callbacks[callbackIdentifier] = callback
    }

    return callbackIdentifier
  }

  // Queue up method calls until app is ready
  const _addToQueue = (method: any, args: [any, ...any]) => {
    _queue.push({ method, args })
  }

  // Determine method of communication with iframe
  const _method = (function () {
    if (_domain === `${window.location}`.split('/').slice(0, 3).join('/')) {
      return 'object'
    } else if (typeof window.postMessage !== 'undefined') {
      return 'post'
    } else {
      return 'none'
    }
  })()

  const get = (prop: string, callback: ({ ...any }) => void, force: boolean) => {
    if (!ready && !force) {
      _addToQueue(get, [prop, callback])

      return
    }

    const cbId = _registerCallback(callback)

    if (_method === 'object') {
      const value = iframe.contentWindow[appName].get.apply(null, [prop])

      if (typeof _callbacks[cbId] !== 'undefined') {
        _callbacks[cbId].apply(null, [value])
      }
    } else if (_method === 'post' && iframe.contentWindow) {
      const messageObject = { f: 'get', args: [prop], cbId }

      iframe.contentWindow.postMessage(JSON.stringify(messageObject), force ? '*' : _domain)
    }
  }

  const set = (prop: string, val: boolean, callback?: ({ ...any }) => void) => {
    if (!ready) {
      _addToQueue(set, [prop, val, callback])

      return
    }

    const cbId = _registerCallback(callback)

    if (_method === 'object') {
      const value = iframe.contentWindow[appName].set.apply(null, [prop, val])

      if (typeof _callbacks[cbId] !== 'undefined') {
        _callbacks[cbId].apply(null, [value])
      }
    } else if (_method === 'post' && iframe.contentWindow) {
      const messageObject = { f: 'set', args: [prop, val], cbId }

      iframe.contentWindow.postMessage(JSON.stringify(messageObject), _domain)
    }
  }

  const bind = (event: string, callback: ({ ...any }) => void, triggerQueue = false) => {
    if (!ready) {
      _addToQueue(bind, [event, callback, true])

      return
    }

    const cbId = _registerCallback(callback)

    if (_method === 'object') {
      iframe.contentWindow[appName].bind.apply(null, [event, callback, triggerQueue])
    } else if (_method === 'post' && iframe.contentWindow) {
      const messageObject = { f: 'bind', args: [event], cbId, triggerQueue }

      iframe.contentWindow.postMessage(JSON.stringify(messageObject), _domain)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const fire = (event: string, obj: {}) => {
    if (!ready) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _addToQueue(fire, [event, obj])

      return
    }

    if (_method === 'object') {
      return iframe.contentWindow[appName].fire.apply(null, [event, obj])
    } else if (_method === 'post' && iframe.contentWindow) {
      const messageObject = { f: 'fire', args: [event, obj] }

      iframe.contentWindow.postMessage(JSON.stringify(messageObject), _domain)
    }
  }

  // Parse messages received from iframe
  const _receiveMessage = (e: MessageEvent) => {
    if (e.origin === _domain) {
      let data

      try {
        data = JSON.parse(e.data)
      } catch (err: unknown) {
        return err
      }

      if (
        typeof data !== 'undefined' &&
        typeof data.cbId !== 'undefined' &&
        typeof _callbacks[data.cbId] === 'function'
      ) {
        _callbacks[data.cbId].apply(null, [data.a, data.b])
      }
    }
  }

  // Listen for message events if need
  //   if (window.addEventListener) {
  window.addEventListener('message', _receiveMessage, false)
  //   } else {
  //     window.attachEvent('onmessage', _receiveMessage)
  //   }

  // Loop through queue when app is ready
  const _processQueue = () => {
    for (let i = 0; i < _queue.length; i += 1) {
      const queueItem = _queue[i]
      queueItem.method.apply(null, queueItem.args)
    }

    _queue = []
    set('queuedEventsProcessed', true)
  }

  // Poll the iframe until the app is bootstrapped
  const _readyInterval = window.setInterval(() => {
    if (!ready && _method === 'object') {
      if (iframe.contentWindow[appName]?.bootstrapped) {
        ready = true
        window.clearInterval(_readyInterval)
        _processQueue()
      }
    } else if (!ready && _method === 'post') {
      get(
        'bootstrapped',
        (bootstrapped) => {
          if (bootstrapped) {
            ready = true
            window.clearInterval(_readyInterval)
            _processQueue()
          }
        },
        true
      )
    }
  }, 100)

  return {
    get,
    set,
    bind,
  }
}

export default glueFrame
